import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseEmptyIcon,
} from '@align-cpf/uikit-icons';
import { ContextualMenu, Icon, InputText } from '../../atoms';
import { IDropdownProps } from './Dropdown.types';
import { createLocators } from '@u1/locators';
import { useDropDown } from './Dropdown.hooks';
import clsx from 'clsx';
import useStyles from './Dropdown.styles';

export const dropdownLocators = createLocators({
  name: 'Dropdown',
  componentLocators: [
    'dropdown',
    'input',
    'iconContainer',
    'iconOpen',
    'iconClose',
    'iconClear',
  ],
});
const { locators } = dropdownLocators();

const Dropdown = <T, AllowClear extends boolean = false>({
  onChange,
  ...props
}: IDropdownProps<T, AllowClear>) => {
  const classes = useStyles();
  const {
    name,
    placeholder = 'Choose an option',
    variant = 'white',
    disabled,
    listPosition = 'bottom',
    autoPosition = false,
    allowClear,
    children,
    ...rest
  } = props;

  const { selectedItem, handleClear, toggleDropdown, isOpen, options } =
    useDropDown({ ...props, onChange, children });

  return (
    <div {...locators.dropdown()} className={classes.root}>
      <ContextualMenu
        isOpen={isOpen}
        position={listPosition}
        onClickOutside={toggleDropdown}
        autoPosition={autoPosition}
        triggerComponent={
          <div className={classes.trigger}>
            <InputText
              {...rest}
              name={name}
              placeholder={placeholder}
              readOnly={true}
              value={selectedItem?.label || ''}
              onClick={toggleDropdown}
              variant={variant}
              _UNSAFE_classname={clsx({
                [classes.input]: true,
                [classes.inputActive]: isOpen,
                [classes.clearable]: selectedItem && allowClear,
              })}
              disabled={disabled}
            />
            <div
              {...locators.iconContainer({ disabled })}
              className={clsx({
                [classes.iconContainer]: true,
                [classes.iconDisabled]: disabled,
              })}
            >
              {selectedItem && allowClear ? (
                <div onClick={handleClear}>
                  <Icon
                    icon={CloseEmptyIcon}
                    className={classes.clear}
                    locatorsOverrides={locators.iconClear()}
                  />
                </div>
              ) : null}
              <div
                onClick={toggleDropdown}
                className={clsx({ [classes.invert]: listPosition === 'top' })}
              >
                {isOpen ? (
                  <Icon
                    icon={ChevronUpIcon}
                    className={classes.chevron}
                    locatorsOverrides={locators.iconClose()}
                  />
                ) : (
                  <Icon
                    icon={ChevronDownIcon}
                    className={classes.chevron}
                    locatorsOverrides={locators.iconOpen()}
                  />
                )}
              </div>
            </div>
          </div>
        }
      >
        {options}
      </ContextualMenu>
    </div>
  );
};

export default Dropdown;
